import { IQService, IScope } from "angular";
import { StateService } from "@uirouter/angularjs";
import { AutomationService } from "../services/automation";
import { localize } from "@gtmhub/localization";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import {
  AutomationCadence,
  AutomationOperationByTargetType,
  AutomationStatus,
  AutomationTriggerPayload,
  AutomationTriggerType,
  AutomationWeekday,
  DayOfMonth,
  IAutomationSchedule,
  ISupportedAutomationTarget,
} from "../models/automation";
import { toKeyMap } from "../../util";
import { IGtmhubRootScopeService } from "../../models";

const DEFAULT_TRIGGER_TYPE = "goal";
const DEFAULT_SELECTED_ACTION = "onEvent";
const DEFAULT_SELECTED_ACTION_EVENT = null;
const DEFAULT_WEEKDAY = "Monday";
const DEFAULT_CADENCE = "daily";
const DEFAULT_MONTH_DAY = 1;

export type AutomationAction = "onEvent" | "manually" | "onSchedule";

interface IAutomationTriggerStateParams {
  automationId: string;
}

export interface IAutomationTriggerScope extends IScope {
  supportedTargets: ISupportedAutomationTarget<AutomationTriggerType>[];
  targets: Record<AutomationTriggerType, ISupportedAutomationTarget<AutomationTriggerType>>;
  selectedItem: AutomationTriggerType;
  selectedItemEvent: AutomationOperationByTargetType[AutomationTriggerType];
  indicators: {
    loading: boolean;
    saving: boolean;
  };
  itemSelection: string;
  showItemSelection: boolean;
  schedule: IAutomationSchedule;
  editMode: boolean;
  selectedAction: AutomationAction;
  selectionStrings: {
    singular: {
      [key in AutomationTriggerType]: string;
    };
    plural: {
      [key in AutomationTriggerType]: string;
    };
    events: {
      [key in AutomationOperationByTargetType[AutomationTriggerType]]: string;
    };
    action: {
      [key in AutomationAction]: string;
    };
    scheduleCadence: {
      [key in AutomationCadence]: string;
    };
    scheduleWeekday: {
      [key in AutomationWeekday]: string;
    };
  };
  predefinedOptions: {
    action: {
      [key in AutomationAction]: key;
    };
    scheduleCadence: {
      [key in AutomationCadence]: key;
    };
    scheduleWeekday: {
      [key in AutomationWeekday]: key;
    };
  };
  triggerTargetTypeChanged: boolean;
  shouldDisplayActionsWarning: boolean;
  selectAction(action: AutomationAction): void;
  selectItem(item: AutomationTriggerType, isEventItem?: boolean): void;
  selectItemEvent(selectedItemEvent: string): void;
  setScheduledCadence(cadence: AutomationCadence): void;
  setScheduledWeekday(weekday: AutomationWeekday): void;
  setScheduledTime(scheduledTime: Date): void;
  setScheduledMonthDay(monthDay: DayOfMonth): void;
  createTriggerEvent(): void;
  resetItemSelection(): void;
}

export class AutomationTriggerCtrl {
  public static $inject = ["$rootScope", "$state", "$scope", "$stateParams", "$q", "AutomationService", "UIErrorHandlingService"];

  constructor(
    private $rootScope: IGtmhubRootScopeService,
    private $state: StateService,
    private $scope: IAutomationTriggerScope,
    private $stateParams: IAutomationTriggerStateParams,
    private $q: IQService,
    private automationService: AutomationService,
    private uiErrorHandlingService: UIErrorHandlingService
  ) {
    this.$scope.indicators = {
      loading: true,
      saving: false,
    };

    this.$scope.selectionStrings = {
      singular: {
        goal: localize("objective_cap"),
        metric: localize("key_result_cap"),
        user: localize("user_cap"),
      },
      plural: {
        goal: localize("objectives_cap"),
        metric: localize("key_results"),
        user: localize("users_cap"),
      },
      events: {
        created: localize("is_created"),
        modified: localize("is_edited"),
        deleted: localize("is_deleted"),
        "progress changed": localize("progress_is_updated"),
        deactivated: localize("is_deactivated"),
      },
      action: {
        onEvent: localize("trigger_on_event"),
        manually: localize("trigger_manually"),
        onSchedule: localize("trigger_schedule"),
      },
      scheduleCadence: {
        daily: localize("daily"),
        weekly: localize("weekly"),
        biweekly: localize("every_two_weeks"),
        monthly: localize("monthly"),
      },
      scheduleWeekday: {
        Monday: localize("monday"),
        Tuesday: localize("tuesday"),
        Wednesday: localize("wednesday"),
        Thursday: localize("thursday"),
        Friday: localize("friday"),
      },
    };
    this.$scope.predefinedOptions = {
      action: {
        onEvent: "onEvent",
        manually: "manually",
        onSchedule: "onSchedule",
      },
      scheduleCadence: {
        daily: "daily",
        weekly: "weekly",
        biweekly: "biweekly",
        monthly: "monthly",
      },
      scheduleWeekday: {
        Monday: "Monday",
        Tuesday: "Tuesday",
        Wednesday: "Wednesday",
        Thursday: "Thursday",
        Friday: "Friday",
      },
    };
    this.$scope.triggerTargetTypeChanged = false;
    this.$scope.shouldDisplayActionsWarning = false;

    this.$scope.selectedItem = DEFAULT_TRIGGER_TYPE;
    this.$scope.selectedItemEvent = DEFAULT_SELECTED_ACTION_EVENT;
    this.$scope.selectedAction = DEFAULT_SELECTED_ACTION;
    this.$scope.itemSelection = null;

    const currentTime = new Date();
    currentTime.setMinutes(0);

    this.$scope.schedule = {
      weekday: DEFAULT_WEEKDAY,
      cadence: DEFAULT_CADENCE,
      monthDay: DEFAULT_MONTH_DAY,
      time: currentTime,
    };

    this.$scope.selectAction = this.selectAction;
    this.$scope.selectItem = this.selectItem;
    this.$scope.selectItemEvent = this.selectItemEvent;
    this.$scope.setScheduledCadence = this.setScheduledCadence;
    this.$scope.setScheduledWeekday = this.setScheduledWeekday;
    this.$scope.setScheduledTime = this.setScheduledTime;
    this.$scope.setScheduledMonthDay = this.setScheduledMonthDay;
    this.$scope.createTriggerEvent = this.createTriggerEvent;
    this.$scope.resetItemSelection = this.resetItemSelection;

    this.init();
  }

  private automationStates: { rules: string } = {
    rules: "gtmhub.automation.rules",
  };
  private oldTriggerTargetType: AutomationTriggerType;

  private init = () => {
    this.$q
      .all({
        currentAutomation: this.automationService.getAutomation(this.$stateParams.automationId),
        supportedTargetRes: this.automationService.getTargetWithActions(),
      })
      .then(
        ({ currentAutomation, supportedTargetRes }) => {
          this.$scope.supportedTargets = supportedTargetRes.supportedTargets;
          this.$scope.targets = toKeyMap("target", this.$scope.supportedTargets);
          const shouldEditExistingTrigger = currentAutomation.trigger;
          if (shouldEditExistingTrigger) {
            this.oldTriggerTargetType = currentAutomation.trigger.objectType;
            this.$scope.editMode = true;

            if (currentAutomation.status === "execute") {
              this.$scope.selectedAction = "manually";
            }

            if (currentAutomation.status === "enabled" || currentAutomation.status === "disabled") {
              if (currentAutomation.trigger.scheduled) {
                this.$scope.selectedAction = "onSchedule";

                this.$scope.schedule = {
                  weekday: currentAutomation.schedule.weekday,
                  monthDay: currentAutomation.schedule.monthDay,
                  cadence: currentAutomation.schedule.cadence,
                  time: currentAutomation.schedule.time,
                };
              } else {
                this.$scope.selectedAction = "onEvent";
              }
            }

            this.$scope.selectedItem = currentAutomation.trigger.objectType;
            this.$scope.selectedItemEvent = currentAutomation.trigger.operation;
            this.$scope.itemSelection = this.createItemSelectionValue();
          }

          this.$scope.indicators.loading = false;
        },
        (err) => this.handleIndicatorError(err, "loading")
      );
  };

  private createItemSelectionValue = (): string => {
    return this.$scope.selectionStrings.singular[this.$scope.selectedItem] + " " + (this.$scope.selectionStrings.events[this.$scope.selectedItemEvent] || "");
  };

  private selectAction = (action: AutomationAction): void => {
    this.$scope.selectedAction = action;
    this.$scope.shouldDisplayActionsWarning = this.$scope.selectedItem === "user" && action === "onEvent";
  };

  private selectItem = (item: AutomationTriggerType, isTriggeredOnEvent = true): void => {
    this.$scope.triggerTargetTypeChanged = item !== this.oldTriggerTargetType;
    this.$scope.shouldDisplayActionsWarning = item === "user" && isTriggeredOnEvent;

    if (isTriggeredOnEvent) {
      this.$scope.showItemSelection = true;

      // if changing already selected item
      if (this.$scope.selectedItem !== item) {
        this.$scope.itemSelection = null;
        this.$scope.selectedItemEvent = DEFAULT_SELECTED_ACTION_EVENT;
      }
    } else {
      // if manual or schedule reset event items combo
      this.$scope.showItemSelection = false;
      this.$scope.itemSelection = null;
      this.$scope.selectedItemEvent = DEFAULT_SELECTED_ACTION_EVENT;
    }

    this.$scope.selectedItem = item;
  };

  private selectItemEvent = (selectedItemEvent: AutomationOperationByTargetType[AutomationTriggerType]): void => {
    this.$scope.selectedItemEvent = selectedItemEvent;
    this.$scope.itemSelection = this.createItemSelectionValue();
    this.$scope.shouldDisplayActionsWarning = this.$scope.selectedItem === "user";
  };

  private setScheduledCadence = (cadence: AutomationCadence): void => {
    this.$scope.schedule.cadence = cadence;
    // if it's "Sunday" or "Saturday" which are not listed in the UI by design
    if (!this.$scope.predefinedOptions.scheduleWeekday[this.$scope.schedule.weekday]) {
      this.$scope.schedule.weekday = DEFAULT_WEEKDAY;
    }
  };

  private setScheduledWeekday = (weekday: AutomationWeekday): void => {
    this.$scope.schedule.weekday = weekday;
  };

  private setScheduledTime = (scheduledTime: Date): void => {
    this.$scope.schedule.time = scheduledTime;
  };

  private setScheduledMonthDay = (monthDay: DayOfMonth): void => {
    this.$scope.schedule.monthDay = monthDay;
  };

  private createTriggerEvent = (): void => {
    this.$scope.indicators.saving = true;
    const manual: boolean = this.$scope.selectedAction === "manually";
    const scheduled: boolean = this.$scope.selectedAction === "onSchedule";
    const status: AutomationStatus = this.$scope.selectedAction === "manually" ? "execute" : "disabled";

    const automationTask: AutomationTriggerPayload = {
      status,
      trigger: {
        objectType: this.$scope.selectedItem,
        operation: this.$scope.selectedItemEvent,
        manual,
        scheduled,
      },
    };

    if (scheduled) {
      automationTask.schedule = {
        cadence: this.$scope.schedule.cadence,
        time: this.$scope.schedule.time,
        weekday: this.$scope.schedule.weekday,
        monthDay: this.$scope.schedule.monthDay,
      };
    }

    this.automationService.createTriggerEvent(this.$stateParams.automationId, automationTask).then(
      () => {
        this.$scope.indicators.saving = false;
        this.$rootScope.$broadcast("automationChanged", { triggerChanged: this.$scope.triggerTargetTypeChanged, changes: automationTask });
        this.$state.go(this.automationStates.rules, { automationId: this.$stateParams.automationId }, { reload: false });
      },
      (err) => this.uiErrorHandlingService.handleIndicator(err, { indicator: "saving", inline: true })
    );
  };

  private resetItemSelection = () => (this.$scope.showItemSelection = false);

  private handleIndicatorError = <R>(rejection: R, indicator: string, inline = false): void => {
    this.uiErrorHandlingService.handleIndicator(rejection, { indicator, inline });
  };
}
